import React from 'react';
import { prop } from 'ramda';
import { format } from 'd3-format';
import { LabelSeries } from 'react-vis';

import { map } from 'helpers/safeRamda';
import { ClassProp } from 'helpers/types';
import { theme } from 'config';
import ReportBase, { ReportParams } from 'scenes/Report/components/ReportBase';
import BarChart from 'scenes/Report/components/BarChart';

import ReplaceTextConfig from './SubscriberPurchasePatterns.batch.config';
import styles from './SubscriberPurchasePatterns.styles';

type Styles = ClassProp<typeof styles>;

type BarValue = {
  cashbackPercentage: number;
  totalPurchases: string;
  percentOfMerchantTotal: number;
  aov: number;
  startDate: string;
  endDate: string;
};

const labelTransformer = map(
  ({ totalPurchases, percentOfMerchantTotal }: BarValue) => ({
    x: totalPurchases,
    y: percentOfMerchantTotal + 2,
    label: format('.1%')(percentOfMerchantTotal / 100),
  }),
);

const labelTransformerAOV = map(
  ({ totalPurchases, percentOfMerchantTotal, aov }: BarValue) => ({
    x: totalPurchases,
    y: percentOfMerchantTotal - 0.5,
    label: `AOV: ${format('$.2f')(aov)}`,
  }),
);

const legendItems = [
  {
    title: 'Number of Transactions',
    color: theme.violet,
  },
];

const tickFormatYAxis = (value: number) => format('.1%')(value / 100);

export const renderBarChart = (
  params: ReportParams,
  header: string,
  batchKey: string,
  title: string,
  segmentValue: number | string | null,
  classes: Styles,
  height: number,
  idx: number,
  purchasesBySubWithMulPurchases: BarValue[],
): JSX.Element => {
  const { startDate, endDate } = purchasesBySubWithMulPurchases[0];
  return (
    <ReportBase
      batchKey={batchKey}
      header={header}
      params={params}
      segmentedBatchData={{
        idx,
        startDate,
        endDate,
        replaceTextConfig: ReplaceTextConfig,
        isSegmented: true,
        segmentValue,
      }}
      key={idx}
    >
      <BarChart
        title={title}
        getAxis={{ x: 'totalPurchases', y: 'percentOfMerchantTotal' }}
        height={height}
        tickFormatYAxis={tickFormatYAxis}
        data={purchasesBySubWithMulPurchases}
        legendItems={legendItems}
        hintFormat=".2%"
      >
        <LabelSeries
          className={classes.labelSeries}
          data={labelTransformer(purchasesBySubWithMulPurchases || [])}
          getLabel={prop('label')}
          labelAnchorX="middle"
        />
        <LabelSeries
          className={classes.labelSeries}
          data={labelTransformerAOV(purchasesBySubWithMulPurchases || [])}
          getLabel={prop('label')}
          labelAnchorX="middle"
        />
      </BarChart>
    </ReportBase>
  );
};
